import type { ExportFormat } from "@/modules/export";

/** Status de acesso do usuário no Hub, alterado pela ativação/inativação (RN01/RN03). */
export type UserStatus = "ativo" | "inativo";

export interface User {
  id: string;
  nome: string;
  email: string;
  /** Celular do diretório corporativo; vazio quando o serviço não informa. */
  celular: string;
  /** Time/área de origem no Entra ID; vazio quando o serviço não informa. */
  time: string;
  /** Vazio quando `papel_id` é nulo — a tela sinaliza "Sem perfil". */
  papelId: string;
  papel: string;
  status: UserStatus;
  /** URL da foto do Entra ID; vazia quando não há imagem. */
  fotoUrl: string;
  /** Data/hora ISO da última atividade; vazia quando não há registro. */
  ultimaAtividade: string;
}

export interface UserFilterOption {
  label: string;
  value: string;
}

/**
 * Telefone no formato do protótipo (+55 24 99900-0000). Um número fora do padrão
 * brasileiro é exibido como veio do serviço, sem tentativa de correção.
 */
export function formatUserPhone(telefone: string): string {
  const value = telefone.trim();
  if (!value) return "";

  // Um prefixo internacional de outro país sai como veio: só o +55 é reescrito.
  if (value.startsWith("+") && !value.startsWith("+55")) return value;

  const digits = value.replace(/\D/g, "");
  const national = digits.length > 11 && digits.startsWith("55") ? digits.slice(2) : digits;
  if (national.length !== 10 && national.length !== 11) return value;

  const ddd = national.slice(0, 2);
  const line = national.slice(2);

  return `+55 ${ddd} ${line.slice(0, line.length - 4)}-${line.slice(-4)}`;
}

/**
 * Colunas de dado ordenáveis. Os Padrões Globais de Interface preveem ordenação
 * pelo cabeçalho em todas as colunas do grid; Foto e Ações não carregam dado
 * comparável e por isso ficam de fora.
 */
export type UsersSortField = "nome" | "email" | "time" | "papel" | "status" | "ultimaAtividade";

export type UsersSortDirection = "asc" | "desc";

/** Ordenação enviada ao serviço em `sort_by`/`sort_direction`. O padrão é Nome (A–Z). */
export interface UsersSort {
  field: UsersSortField;
  direction: UsersSortDirection;
}

export const USERS_SORT_FIELDS: UsersSortField[] = [
  "nome",
  "email",
  "time",
  "papel",
  "status",
  "ultimaAtividade",
];

export function isUsersSortField(value: unknown): value is UsersSortField {
  return USERS_SORT_FIELDS.includes(value as UsersSortField);
}

export const DEFAULT_USERS_SORT: UsersSort = { field: "nome", direction: "asc" };

/** Valores aceitos pelo serviço no corpo de `PATCH .../status`. */
export const USER_STATUS_PAYLOAD: Record<UserStatus, string> = {
  ativo: "Ativo",
  inativo: "Inativo",
};

/** Rótulo do status na tela: a listagem e os modais escrevem Ativo ou Inativo. */
export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  ativo: "Ativo",
  inativo: "Inativo",
};

export interface ListUsersParams {
  pesquisa?: string;
  /** Times escolhidos; vazio não restringe. Vai como lista, como o `claims` de Perfis. */
  time?: string[];
  /** Perfis escolhidos (id); vazio não restringe. */
  papelId?: string[];
  page?: number;
  pageSize?: number;
  sort?: UsersSort;
  signal?: AbortSignal;
}

/** Critérios da visualização atual, sem paginação, e o formato de saída. */
export interface ExportUsersParams {
  pesquisa?: string;
  time?: string[];
  papelId?: string[];
  sort?: UsersSort;
  /** Rótulos dos filtros da tela, impressos no arquivo. */
  filtros?: string[];
  /** Colunas visíveis da tela, na ordem; o gateway as traduz para as chaves do core. */
  colunas?: readonly UsersColumnKey[];
  format: ExportFormat;
  signal?: AbortSignal;
}

/** RN04: a alteração de status parte da confirmação do administrador no modal. */
export interface UpdateUserStatusParams {
  id: string;
  status: UserStatus;
  signal?: AbortSignal;
}

/** Vínculo de perfil do usuário: o par que a listagem exibe na coluna Perfil. */
export interface UserRole {
  papelId: string;
  papel: string;
}

/** RN01/RN02: a operação só carrega o perfil; a identidade é imutável (RN03). */
export interface UpdateUserRoleParams {
  id: string;
  papelId: string;
  signal?: AbortSignal;
}

export interface UsersPage {
  users: User[];
  /** Total de registros que atendem aos critérios, e não apenas os da página. */
  total: number;
  /** Usuários sem perfil na base filtrada, sinalizados no cabeçalho da coluna Perfil; `null` quando o serviço não informa. */
  totalSemPerfil: number | null;
  times: UserFilterOption[];
  papeis: UserFilterOption[];
}

/** Colunas da tela de Usuários, na ordem da tabela (a exportação traduz cada uma para a chave do core). */
export type UsersColumnKey =
  "foto" | "nome" | "email" | "time" | "papel" | "ultimaAtividade" | "status" | "acoes";
